// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './user-state';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import { type SubscriptionsResp } from '@shared/data-access/interfaces/api/engine/subscriptions';
import {
  type AdvisorRole,
  type Organization,
  type LoginType,
  type UserBase,
} from '@shared/data-access/interfaces/api/engine/citec';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api';

export const userSlice = createSlice({
  initialState: () => initialState,
  name: 'user',
  reducers: {
    reset: () => initialState,
    setCurrency: (state, { payload }: PayloadAction<AccountCurrency>) => {
      state.accountCurrency = payload;
    },
    setUserData: (state, action: PayloadAction<SubscriptionsResp>) => {
      state.status = action.payload.status;
      state.expiration_date = Number(
        +action.payload.timestamp_expiration_date! * 1000,
      );
    },
    setOrganizations: (state, action: PayloadAction<Organization[]>) => {
      state.organizations = action.payload;
    },
    setLoginType: (state, action: PayloadAction<LoginType>) => {
      state.loginType = action.payload;
    },
    setUserBase: (state, action: PayloadAction<UserBase>) => {
      state.userBase = { ...state.userBase, ...action.payload };
    },
    setDashboard: (state, action: PayloadAction<'broker' | 'aggregator'>) => {
      state.dashboard = action.payload;
    },
    setRole: (state, action: PayloadAction<AdvisorRole>) => {
      state.role = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      advisoryApi.endpoints.getCitecUserClientProfile.matchFulfilled,
      (state, { payload }) => {
        state.profile = payload;
      },
    );
  },
});
